/**
 * AI Analysis Card
 *
 * Отображает AI-анализ контакта от OSINT-провайдера: текстовый портрет
 * (profile_analyzer) и визуальный анализ изображений (visual_analyzer).
 *
 * Появилось в провайдере в мае 2026 — новые модули profile_analyzer и
 * visual_analyzer приходят отдельно от стандартных 17 источников.
 */

import React, { useState } from "react";
import { ChevronDown, ChevronUp, Sparkles, Image as ImageIcon, AlertTriangle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import type { AiAnalysisData, ProfileAnalyzerData, VisualAnalyzerData } from "@/types/osint";
import { cn } from "@/utils";

interface AiAnalysisCardProps {
  data: AiAnalysisData | null | undefined;
  className?: string;
}

const hasProfileData = (profile?: ProfileAnalyzerData): boolean => {
  if (!profile) return false;
  return Boolean(
    profile.summary ||
    (profile.interests && profile.interests.length > 0) ||
    (profile.demographics && Object.keys(profile.demographics).length > 0) ||
    (profile.identity_matches && profile.identity_matches.length > 0) ||
    (profile.risk_flags && profile.risk_flags.length > 0),
  );
};

const hasVisualData = (visual?: VisualAnalyzerData): boolean => {
  if (!visual) return false;
  if (visual.per_image && visual.per_image.length > 0) return true;
  const s = visual.summary;
  if (!s) return false;
  return Boolean(
    (s.descriptions && s.descriptions.length > 0) ||
    s.modal_age_range ||
    s.modal_gender ||
    s.modal_setting ||
    (s.location_cues && s.location_cues.length > 0) ||
    (s.occupation_cues && s.occupation_cues.length > 0) ||
    (s.notable_objects && s.notable_objects.length > 0) ||
    (s.visible_text && s.visible_text.length > 0) ||
    (s.risk_signals && s.risk_signals.length > 0),
  );
};

const Chip: React.FC<{ children: React.ReactNode; tone?: "default" | "warning" }> = ({
  children,
  tone = "default",
}) => (
  <span
    className={cn(
      "inline-flex items-center px-2 py-0.5 rounded-full text-fs-2xs font-medium",
      tone === "warning"
        ? "bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300"
        : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    )}
  >
    {children}
  </span>
);

const ProfileBlock: React.FC<{ profile: ProfileAnalyzerData }> = ({ profile }) => (
  <div className="space-y-2.5">
    {profile.summary && <p className="text-xs text-ink-2 leading-relaxed whitespace-pre-line">{profile.summary}</p>}

    {profile.interests && profile.interests.length > 0 && (
      <div>
        <div className="text-fs-2xs font-semibold text-ink-2 uppercase tracking-wide mb-1">Интересы</div>
        <div className="flex flex-wrap gap-1">
          {profile.interests.map((interest, idx) => (
            <Chip key={`${interest}-${idx}`}>{interest}</Chip>
          ))}
        </div>
      </div>
    )}

    {profile.demographics && Object.keys(profile.demographics).length > 0 && (
      <div>
        <div className="text-fs-2xs font-semibold text-ink-2 uppercase tracking-wide mb-1">Демография</div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          {Object.entries(profile.demographics).map(([key, value]) => (
            <div key={key} className="flex items-baseline gap-1">
              <span className="text-ink-3">{key}:</span>
              <span className="text-ink-1 truncate">
                {typeof value === "object" ? JSON.stringify(value) : String(value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    )}

    {profile.risk_flags && profile.risk_flags.length > 0 && (
      <div>
        <div className="text-fs-2xs font-semibold text-warning-700 dark:text-warning-300 uppercase tracking-wide mb-1 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          Риск-флаги ({profile.risk_flags.length})
        </div>
        <ul className="space-y-1">
          {profile.risk_flags.map((flag, idx) => {
            const message =
              (flag && typeof flag === "object" && (flag.message || flag.description || flag.text || flag.category)) ||
              JSON.stringify(flag);
            return (
              <li key={idx} className="text-xs text-warning-700 dark:text-warning-300">
                — {String(message)}
              </li>
            );
          })}
        </ul>
      </div>
    )}

    {profile.identity_matches && profile.identity_matches.length > 0 && (
      <div>
        <div className="text-fs-2xs font-semibold text-ink-2 uppercase tracking-wide mb-1">
          Возможные совпадения личности ({profile.identity_matches.length})
        </div>
        <ul className="space-y-1">
          {profile.identity_matches.map((match, idx) => {
            const name =
              (match && typeof match === "object" && (match.name || match.full_name || match.identity)) ||
              JSON.stringify(match);
            const confidence =
              match && typeof match === "object" && typeof match.confidence !== "undefined" ? match.confidence : null;
            return (
              <li key={idx} className="text-xs text-ink-2">
                <span className="font-medium">{String(name)}</span>
                {confidence !== null && (
                  <span className="ml-1 text-ink-3">
                    ({typeof confidence === "number" ? `${Math.round(confidence * 100)}%` : String(confidence)})
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    )}
  </div>
);

const VisualBlock: React.FC<{ visual: VisualAnalyzerData }> = ({ visual }) => {
  const s = visual.summary;
  return (
    <div className="space-y-2.5">
      {s && (
        <>
          {s.descriptions && s.descriptions.length > 0 && (
            <ul className="space-y-1">
              {s.descriptions.map((d, idx) => (
                <li key={idx} className="text-xs text-ink-2">
                  — {d}
                </li>
              ))}
            </ul>
          )}

          {(s.modal_age_range || s.modal_gender || s.modal_setting) && (
            <div className="flex flex-wrap gap-1.5">
              {s.modal_age_range && <Chip>возраст: {s.modal_age_range}</Chip>}
              {s.modal_gender && <Chip>пол: {s.modal_gender}</Chip>}
              {s.modal_setting && <Chip>обстановка: {s.modal_setting}</Chip>}
            </div>
          )}

          {((s.location_cues && s.location_cues.length > 0) ||
            (s.occupation_cues && s.occupation_cues.length > 0) ||
            (s.notable_objects && s.notable_objects.length > 0) ||
            (s.visible_text && s.visible_text.length > 0)) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              {s.location_cues && s.location_cues.length > 0 && (
                <div>
                  <div className="text-fs-2xs font-semibold text-ink-2 uppercase tracking-wide mb-0.5">Локация</div>
                  <div className="text-ink-2">{s.location_cues.join(", ")}</div>
                </div>
              )}
              {s.occupation_cues && s.occupation_cues.length > 0 && (
                <div>
                  <div className="text-fs-2xs font-semibold text-ink-2 uppercase tracking-wide mb-0.5">Занятость</div>
                  <div className="text-ink-2">{s.occupation_cues.join(", ")}</div>
                </div>
              )}
              {s.notable_objects && s.notable_objects.length > 0 && (
                <div>
                  <div className="text-fs-2xs font-semibold text-ink-2 uppercase tracking-wide mb-0.5">Объекты</div>
                  <div className="text-ink-2">{s.notable_objects.join(", ")}</div>
                </div>
              )}
              {s.visible_text && s.visible_text.length > 0 && (
                <div>
                  <div className="text-fs-2xs font-semibold text-ink-2 uppercase tracking-wide mb-0.5">
                    Видимый текст
                  </div>
                  <div className="text-ink-2">{s.visible_text.join(", ")}</div>
                </div>
              )}
            </div>
          )}

          {s.risk_signals && s.risk_signals.length > 0 && (
            <div>
              <div className="text-fs-2xs font-semibold text-warning-700 dark:text-warning-300 uppercase tracking-wide mb-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Визуальные риск-сигналы
              </div>
              <ul className="space-y-1">
                {s.risk_signals.map((signal, idx) => (
                  <li key={idx} className="text-xs text-warning-700 dark:text-warning-300">
                    — {signal}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {visual.per_image && visual.per_image.length > 0 && (
        <div>
          <div className="text-fs-2xs font-semibold text-ink-2 uppercase tracking-wide mb-1 flex items-center gap-1">
            <ImageIcon className="w-3 h-3" />
            По изображениям ({visual.per_image.length})
          </div>
          <ul className="space-y-1.5">
            {visual.per_image.map((img, idx) => (
              <li key={idx} className="text-xs text-ink-2 pl-2 border border-blue-200 dark:border-blue-800 border-line">
                {img._source && <span className="text-fs-2xs text-ink-3 uppercase mr-1">{img._source}:</span>}
                {img.description || "—"}
                {(img.apparent_age_range || img.apparent_gender || img.setting) && (
                  <span className="ml-1 text-ink-3">
                    ({[img.apparent_age_range, img.apparent_gender, img.setting].filter(Boolean).join(", ")})
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/**
 * Карточка AI-анализа (profile_analyzer + visual_analyzer).
 * Возвращает null, если данных нет.
 */
export const AiAnalysisCard: React.FC<AiAnalysisCardProps> = ({ data, className }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  if (!data) return null;

  const showProfile = hasProfileData(data.profile_analyzer);
  const showVisual = hasVisualData(data.visual_analyzer);

  if (!showProfile && !showVisual) return null;

  return (
    <div className={cn("rounded-card border border-line overflow-hidden bg-surface-elevated", className)}>
      <button
        onClick={() => setIsExpanded((v) => !v)}
        className="w-full px-3 py-2 flex items-center justify-between hover:bg-white/40 dark:hover:bg-dark-700/30 transition-colors"
        type="button"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          <span className="text-xs font-semibold text-blue-700 dark:text-blue-300">AI-анализ профиля</span>
          {showProfile && <Chip>портрет</Chip>}
          {showVisual && <Chip>изображения</Chip>}
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-blue-600 dark:text-blue-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-blue-600 dark:text-blue-400" />
        )}
      </button>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 space-y-3">
              {showProfile && data.profile_analyzer && <ProfileBlock profile={data.profile_analyzer} />}
              {showProfile && showVisual && <div className="border-t border-blue-200/60 dark:border-blue-900/60" />}
              {showVisual && data.visual_analyzer && <VisualBlock visual={data.visual_analyzer} />}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AiAnalysisCard;
